// src/encounter/intelligence/use-explain.ts
//
// The Explain card's request: POST /api/intelligence/explain (scrips-clinician-demo explainHttp.ts at c1728c4) with the
// signed-in clinician's own token, the same auth pattern as the engine client (intelligence-engine.ts). Body:
// { patientId, encounterId (required, may be null), requestId, system, appointmentId? }. One JSON reply, no streaming.
// appointmentId (live 4 Nov): sent beside an encounter id so the engine can confirm a just-arrived encounter, which is not
// yet in the patient's encounter list; never sent without an encounter id. The binding stays the encounter id.
//
// Encounter owner rulings it holds:
//   A2  the payload is checked when it arrives; a malformed one is the technical line, never the body.
//   A7  each request carries a fresh request id; a reply is dropped unless it answers the open request for the open
//       patient; the card clears the moment the patient or encounter changes.
//   A12 one request in flight; cancel on unmount or context change; sanitised words that tell apart access denied,
//       consent withdrawn, withheld and technical failure. The engine's error bodies are never shown.
//   duplicate-request: "No new copy. The first request's card stays."
//   consent-withdrawn: the explanation is cleared first, then the alert.
// Nothing here logs: no patient id, payload or error text reaches a console or telemetry.
import { useCallback, useEffect, useRef, useState } from 'react';
import { authStorage } from '@/auth/storage';
import { INTELLIGENCE_BASE } from '../intelligence-engine';
import { SESSION_EXPIRED, STATE_LINE, parseExplanation, type ExplanationV1 } from './explanation';
import { withTransientRetry } from './engine-retry';

export const EXPLAIN_PATH = `${INTELLIGENCE_BASE}/explain`;

/** The body systems the engine accepts (explainRoute.ts EXPLAIN_SYSTEMS). */
export type ExplainSystem = 'heart-vessels' | 'metabolism' | 'liver-kidney-blood' | 'hormones' | 'inflammation-immunity' | 'brain-nervous' | 'sleep-recovery-airway';

type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export type ExplainStatus = 'idle' | 'loading' | 'done';

export interface ExplainView {
  status: ExplainStatus;
  /** The checked payload to draw (answered, withheld, denied or failed). */
  explanation: ExplanationV1 | null;
  /** A fixed line for a refusal that carried no payload (an expired session, a proxy error, a malformed reply). */
  notice: string | null;
}

const IDLE: ExplainView = { status: 'idle', explanation: null, notice: null };

/** A request id the engine accepts (REQUEST_ID = /^[A-Za-z0-9_-]{8,64}$/): random, never derived from patient data. */
export function newRequestId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Fallback without randomUUID: an RFC 4122 v4 GUID from random bytes (the decisions route needs the hyphenated form).
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** What a reply becomes. Pure, so the hook and its tests read one rule. `null` = keep what is on screen. */
export function outcomeOf(status: number, body: unknown, expect: { requestId: string; patientId: string; encounterId?: string | null }): { explanation: ExplanationV1 | null; notice: string | null } | null {
  const x = parseExplanation(body);
  if (x) {
    // A7: a card bound to another encounter is never drawn in this visit (its readings are encounter-scoped).
    if ('encounterId' in expect && (x.state === 'answered' || x.state === 'withheld')) {
      const want = (expect.encounterId ?? '').toLowerCase(), got = (x.binding.encounterId ?? '').toLowerCase();
      if (want !== got) return { explanation: null, notice: STATE_LINE.technical };
    }
    // A7: a reply for another request or another patient is never drawn.
    if (x.requestId !== expect.requestId || x.binding.patientId.toLowerCase() !== expect.patientId.toLowerCase()) {
      // A refusal is written before the patient is bound (an empty patient id); it is still this request's answer.
      if (!(x.requestId === expect.requestId && x.state !== 'answered' && x.state !== 'withheld' && x.binding.patientId === '')) return { explanation: null, notice: STATE_LINE.technical };
    }
    if (x.reason === 'duplicate-request') return null;
    return { explanation: x, notice: null };
  }
  if (status === 401) return { explanation: null, notice: SESSION_EXPIRED };
  if (status === 429) return { explanation: null, notice: STATE_LINE.busy };
  if (status === 504) return { explanation: null, notice: STATE_LINE.timeout };
  if (status === 409) return null;
  return { explanation: null, notice: STATE_LINE.technical };
}

export interface UseExplainOptions {
  patientId: string | null | undefined;
  encounterId?: string | null;
  /** The visit's appointment id; sent only beside an encounter id. */
  appointmentId?: string | null;
  fetch?: FetchLike;
  /** Test seam; the default reads the signed-in clinician's token at call time. */
  token?: () => string | null;
  newId?: () => string;
}

/** One Explain request at a time, for the open patient and encounter. */
export function useExplain({ patientId, encounterId = null, appointmentId = null, fetch: fetchImpl, token, newId = newRequestId }: UseExplainOptions) {
  // The view belongs to one patient and encounter: a view kept for another context is never shown (A7).
  const context = `${patientId ?? ''}|${encounterId ?? ''}`;
  const [kept, setView] = useState<ExplainView & { context: string }>({ ...IDLE, context });
  // A context change (patient or encounter, including a return to an earlier one) starts from idle: the kept view is
  // replaced during render (React's "adjust state on a prop change" pattern), so no old answer or stale "loading"
  // can come back.
  if (kept.context !== context) setView({ ...IDLE, context });
  const view: ExplainView = kept.context === context ? kept : IDLE;
  const inFlight = useRef<AbortController | null>(null);

  // A7: a patient or encounter change (and unmount) cancels the request in flight.
  useEffect(
    () => () => {
      inFlight.current?.abort();
      inFlight.current = null;
    },
    [patientId, encounterId],
  );

  const run = useCallback(
    async (system: ExplainSystem) => {
      if (!patientId || inFlight.current) return; // A12: one request in flight.
      const access = (token ?? (() => authStorage.getAccessToken()))();
      if (!access) {
        setView({ status: 'done', explanation: null, notice: SESSION_EXPIRED, context });
        return;
      }
      const ac = new AbortController();
      inFlight.current = ac;
      setView((v) => (v.context === context ? { ...v, status: 'loading', notice: null } : { ...IDLE, status: 'loading', context }));
      const f = fetchImpl ?? ((input: string, init: RequestInit) => fetch(input, init));
      let next: ReturnType<typeof outcomeOf>;
      try {
        // engine-retry.ts: a transient refusal (429, 502-504, the network) is sent again while the card keeps loading.
        // Each send carries its own request id (A7), so the engine never answers a resend as a duplicate of the refused one.
        const { res, requestId } = await withTransientRetry(
          async () => {
            const id = newId();
            const r = await f(EXPLAIN_PATH, {
              method: 'POST',
              headers: { authorization: `Bearer ${access}`, 'content-type': 'application/json', accept: 'application/json' },
              body: JSON.stringify({ patientId, encounterId: encounterId ?? null, requestId: id, system, ...(encounterId && appointmentId ? { appointmentId } : {}) }),
              signal: ac.signal,
            });
            return { res: r, requestId: id };
          },
          (a) => a.res.status,
          ac.signal,
        );
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          body = null; // Not JSON (a proxy page): the status alone decides the line below.
        }
        next = outcomeOf(res.status, body, { requestId, patientId, encounterId: encounterId ?? null });
      } catch {
        // Cancelled (unmount, context change) or the network failed: a cancelled request draws nothing.
        next = ac.signal.aborted ? null : { explanation: null, notice: STATE_LINE.technical };
      }
      if (ac.signal.aborted) return;
      inFlight.current = null;
      setView((v) => {
        if (v.context !== context) return v;
        if (!next) return { ...v, status: 'done' };
        // consent-withdrawn: the earlier explanation is cleared first; the alert replaces it.
        return { status: 'done', explanation: next.explanation, notice: next.notice, context };
      });
    },
    [patientId, encounterId, appointmentId, context, fetchImpl, token, newId],
  );

  const cancel = useCallback(() => {
    inFlight.current?.abort();
    inFlight.current = null;
    setView((v) => ({ ...v, status: v.explanation || v.notice ? 'done' : 'idle' }));
  }, []);

  return { ...view, run, cancel };
}
